import type { PollWithRespondents } from '~/shared/types';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function asJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const message =
      data && typeof data === 'object' && data && 'error' in data
        ? String((data as { error: unknown }).error)
        : `request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return data as T;
}

export interface CreatePollBody {
  title: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  expiresAt: number | null;
}

export interface CreatePollResult {
  id: string;
  ownerToken: string;
  createdAt: number;
}

export async function createPoll(body: CreatePollBody): Promise<CreatePollResult> {
  const res = await fetch('/api/polls', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return asJson<CreatePollResult>(res);
}

export async function getPoll(id: string): Promise<PollWithRespondents> {
  const res = await fetch(`/api/polls/${encodeURIComponent(id)}`);
  return asJson<PollWithRespondents>(res);
}

export interface CreateRespondentBody {
  name: string;
  yes: string[];
  maybe?: string[];
}

export interface CreateRespondentResult {
  id: string;
  editToken: string;
  updatedAt: number;
}

export async function createRespondent(
  pollId: string,
  body: CreateRespondentBody,
): Promise<CreateRespondentResult> {
  const res = await fetch(`/api/polls/${encodeURIComponent(pollId)}/respondents`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return asJson<CreateRespondentResult>(res);
}

export interface UpdateRespondentBody {
  name?: string;
  yes?: string[];
  maybe?: string[];
}

export async function updateRespondent(
  pollId: string,
  respId: string,
  editToken: string,
  body: UpdateRespondentBody,
): Promise<{ ok: true }> {
  const res = await fetch(
    `/api/polls/${encodeURIComponent(pollId)}/respondents/${encodeURIComponent(respId)}`,
    {
      method: 'PATCH',
      headers: {
        'content-type': 'application/json',
        'x-edit-token': editToken,
      },
      body: JSON.stringify(body),
    },
  );
  return asJson<{ ok: true }>(res);
}

export async function deleteRespondent(
  pollId: string,
  respId: string,
  editToken: string,
): Promise<{ ok: true }> {
  const res = await fetch(
    `/api/polls/${encodeURIComponent(pollId)}/respondents/${encodeURIComponent(respId)}`,
    {
      method: 'DELETE',
      headers: { 'x-edit-token': editToken },
    },
  );
  return asJson<{ ok: true }>(res);
}
